import React, { useCallback, useEffect, useState } from "react";
import classNames from "classnames";
import { Formik } from "formik";
import { Helmet } from "react-helmet";
import {
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Input,
  InputGroup,
  Label,
  Row,
} from "reactstrap";
import Select from "react-select";
import InputMask from "react-input-mask";
import DatePicker, { registerLocale } from "react-datepicker";
import ru from "date-fns/locale/ru";
import ReactInputMask from "react-input-mask";
import { toast } from "react-toastify";
import { Trash } from "react-feather";

import "react-datepicker/dist/react-datepicker.css";

import { HeaderPage } from "components/HeaderPage";
import { MySpinner } from "components/MySpinner";
import { TableButton } from "components/TableButton";
import { SelectProvider } from "components/SelectProvider";
import { useUser } from "features/Users/UserContext";
import { getSettings, getProvider, getProviders, updateSettings } from "services/api";
import { getWarehouses } from "services/apiWarehouse";

import "./settings.scss";

registerLocale("ru", ru);

const parseDate = (val) => {
  if (!val) return null;
  const d = typeof val === "string" ? new Date(val) : val;
  return isNaN(d.getTime()) ? null : d;
};

const ensureArray = (val) => (Array.isArray(val) ? val : val ? [val] : []);

export const SettingsPage = () => {
  const { admins, user } = useUser();
  const branchIndex = user?.branch != null ? Number(user.branch) : 1;
  const [branch, setBranch] = useState(null);
  const [loading, setLoading] = useState(false);
  const [warehouses, setWarehouses] = useState([]);
  const [providersList, setProvidersList] = useState([]);
  const [ourProviderOption, setOurProviderOption] = useState(null);

  const getAndSetBranch = useCallback(() => {
    setLoading(true);
    getSettings(branchIndex)
      .then((res) => setBranch(res))
      .catch((e) => toast.error(e.response?.data?.message || "Ошибка загрузки настроек"))
      .finally(() => setLoading(false));
  }, [branchIndex]);

  useEffect(() => {
    getAndSetBranch();
  }, [getAndSetBranch]);

  useEffect(() => {
    getWarehouses()
      .then((res) => setWarehouses(Array.isArray(res) ? res : res?.items ?? []))
      .catch(() => setWarehouses([]));
  }, []);

  useEffect(() => {
    getProviders({ paginationCount: 500, paginationOffset: 0 })
      .then((res) => setProvidersList(Array.isArray(res) ? res : res?.items ?? []))
      .catch(() => setProvidersList([]));
  }, []);

  useEffect(() => {
    if (branchIndex !== 1) {
      setOurProviderOption(null);
      return;
    }
    const id = branch?.settings?.ourProvider;
    if (!id) {
      setOurProviderOption(null);
      return;
    }
    getProvider(id)
      .then((res) => setOurProviderOption({ value: res.id, label: res.name }))
      .catch(() => setOurProviderOption({ value: id, label: id }));
  }, [branchIndex, branch?.settings?.ourProvider]);


  const settings = branch?.settings ?? {};
  const initialValues = {
    id: branch?.id ?? "",
    index: branch?.index ?? branchIndex,
    requisites: branch?.requisites ?? {},
    dateOfProhibition: parseDate(settings.dateOfProhibition) ?? null,
    defaultManager: settings.defaultManager ?? "",
    defaultWarehouse: settings.defaultWarehouse ?? "",
    storageWarehouse: settings.storageWarehouse ?? "",
    phone: settings.phone ?? "",
    pickup: settings.pickup ?? "",
    emailsForOrders: ensureArray(settings.emailsForOrders),
    ourUser: settings.ourUser ?? "",
    ourProvider: settings.ourProvider ?? "",
    defaultStoreKeeper: settings.defaultStoreKeeper ?? "",
    emailReportMinStock: ensureArray(settings.emailReportMinStock),
    newRegistrationUser: ensureArray(settings.newRegistrationUser),
    warehousesSearchAdmin: ensureArray(settings.warehousesSearchAdmin),
    warehousesSearchCustomer: ensureArray(settings.warehousesSearchCustomer),
    marketPriceWarehouses: ensureArray(settings.marketPriceWarehouses),
    marketPriceProviders: ensureArray(settings.marketPriceProviders),
  };

  const submitHandler = (values) => {
    const settingsPayload = {
      ...settings,
      ...(branchIndex === 1 && {
        dateOfProhibition: values.dateOfProhibition
          ? (values.dateOfProhibition instanceof Date
            ? values.dateOfProhibition.toISOString().slice(0, 10)
            : values.dateOfProhibition)
          : null,
        defaultManager: values.defaultManager || null,
        ourProvider: values.ourProvider || null,
        newRegistrationUser: values.newRegistrationUser?.filter(Boolean) ?? [],
      }),
      defaultWarehouse:
        values.defaultWarehouse != null && values.defaultWarehouse !== ""
          ? values.defaultWarehouse
          : null,
      storageWarehouse: values.storageWarehouse || null,
      phone: values.phone || null,
      pickup: values.pickup || null,
      emailsForOrders: values.emailsForOrders?.filter(Boolean) ?? [],
      ourUser: values.ourUser || null,
      defaultStoreKeeper: values.defaultStoreKeeper || null,
      emailReportMinStock: values.emailReportMinStock?.filter(Boolean) ?? [],
      warehousesSearchAdmin: values.warehousesSearchAdmin?.filter(Boolean) ?? [],
      warehousesSearchCustomer: values.warehousesSearchCustomer?.filter(Boolean) ?? [],
      marketPriceWarehouses: values.marketPriceWarehouses?.filter(Boolean) ?? [],
      marketPriceProviders: (values.marketPriceProviders ?? [])
        .filter((p) => p && (typeof p === 'object' ? p.id : p))
        .map((p) => typeof p === 'object' && p !== null && 'id' in p ? { id: p.id, name: p.name } : { id: p, name: String(p) }),
    };
    updateSettings({
      index: values.index,
      requisites: values.requisites,
      settings: settingsPayload,
    })
      .then(() => {
        toast.success("Настройки сохранены");
        getAndSetBranch();
      })
      .catch((e) => toast.error(e.response?.data?.message || "Ошибка сохранения"));
  };

  const arrayField = (name, label, placeholder, values, setFieldValue) => {
    const list = values[name] ?? [];
    return (
      <>
        <Row className="mt-3">
          <Col md="12" className="elem-field">
            <h5 className="mb-2">{label}</h5>
            <table className="table table-hover">
              <tbody>
                {list.map((val, i) => (
                  <tr key={`${val}-${i}`}>
                    <td>{val}</td>
                    <td>
                      <TableButton
                        Icon={Trash}
                        onClick={() =>
                          setFieldValue(
                            name,
                            list.filter((_, idx) => idx !== i)
                          )
                        }
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <InputGroup>
              <Input
                type="text"
                placeholder={placeholder}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    const v = e.target.value?.trim();
                    if (v) {
                      setFieldValue(name, [...list, v]);
                      e.target.value = "";
                    }
                  }
                }}
              />
              <Button
                type="button"
                onClick={(e) => {
                  const input = e.target.closest(".input-group")?.querySelector("input");
                  const v = input?.value?.trim();
                  if (v) {
                    setFieldValue(name, [...list, v]);
                    if (input) input.value = "";
                  }
                }}
              >
                Добавить
              </Button>
            </InputGroup>
          </Col>
        </Row>
      </>
    );
  };

  const warehouseArrayField = (name, label, values, setFieldValue) => {
    const list = values[name] ?? [];
    const warehouseOptions = warehouses
      .filter((w) => !list.includes(w.id))
      .map((w) => ({ label: w.name, value: w.id }));
    const getWarehouseLabel = (id) => warehouses.find((w) => w.id === id)?.name ?? id;
    return (
      <>
        <Row className="mt-3">
          <Col md="12" className="elem-field">
            <h5 className="mb-2">{label}</h5>
            <table className="table table-hover">
              <tbody>
                {list.map((id, i) => (
                  <tr key={`${id}-${i}`}>
                    <td>{getWarehouseLabel(id)}</td>
                    <td>
                      <TableButton
                        Icon={Trash}
                        onClick={() =>
                          setFieldValue(
                            name,
                            list.filter((_, idx) => idx !== i)
                          )
                        }
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <Select
              placeholder="Выберите склад"
              className="react-select-container"
              classNamePrefix="react-select"
              options={warehouseOptions}
              value={null}
              onChange={(val) => {
                if (val?.value) setFieldValue(name, [...list, val.value]);
              }}
              isClearable
            />
          </Col>
        </Row>
      </>
    );
  };

  const providerArrayField = (name, label, values, setFieldValue) => {
    const list = values[name] ?? [];
    const selectedIds = list.map((p) => typeof p === 'object' && p !== null ? p.id : p);
    return (
      <>
        <Row className="mt-3">
          <Col md="12" className="elem-field">
            <h5 className="mb-2">{label}</h5>
            <table className="table table-hover">
              <tbody>
                {list.map((provider, i) => {
                  const id = typeof provider === 'object' && provider !== null ? provider.id : provider;
                  const providerName = typeof provider === 'object' && provider !== null ? provider.name : String(provider);
                  return (
                    <tr key={`${id}-${i}`}>
                      <td>{providerName}</td>
                      <td>
                        <TableButton
                          Icon={Trash}
                          onClick={() =>
                            setFieldValue(
                              name,
                              list.filter((_, idx) => idx !== i)
                            )
                          }
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <SelectProvider
              provider={null}
              setProvider={(val) => {
                if (val?.value && !selectedIds.includes(val.value)) {
                  const newProvider = { id: val.value, name: val.label };
                  setFieldValue(name, [...list, newProvider]);
                }
              }}
            />
          </Col>
        </Row>
      </>
    );
  };

  return (
    <Container fluid className="settings p-0">
      <Helmet>
        <title>Настройки в текущем филиале</title>
      </Helmet>

      {loading && <MySpinner overlay />}

      <Formik
        initialValues={initialValues}
        onSubmit={(values, { setSubmitting }) => {
          submitHandler(values);
          setSubmitting(false);
        }}
        enableReinitialize
      >
        {({ values, handleSubmit, setFieldValue }) => (
          <form onSubmit={handleSubmit}>
            <HeaderPage title="Настройки в текущем филиале" />

            <Card className={classNames("settingsCard", "mb-0")}>
              <CardBody>
                {branchIndex === 1 && (
                  <Row>
                    <Col md="8" className="elem-field">
                      <Label className="text-sm-right">Дата запрета правки документов</Label>
                      <div className="d-flex justify-content-between">
                        <DatePicker
                          placeholderText="__.__.____"
                          selected={values.dateOfProhibition}
                          onChange={(val) => setFieldValue("dateOfProhibition", val)}
                          dateFormat="dd.MM.yyyy"
                          locale="ru"
                          className="form-control date"
                          customInput={<ReactInputMask mask={"99.99.9999"} />}
                        />
                      </div>
                    </Col>
                  </Row>
                )}

                {branchIndex === 1 && (
                  <Row>
                    <Col md="8" className="elem-field">
                      <Label className="text-sm-right">Менеджер по умолчанию при регистрации клиента</Label>
                      <Select
                        placeholder="Выберите менеджера"
                        className="react-select-container"
                        classNamePrefix="react-select"
                        options={admins ?? []}
                        value={
                          (admins ?? []).find((a) => a.value === values.defaultManager) ?? null
                        }
                        onChange={(val) => setFieldValue("defaultManager", val?.value ?? "")}
                        isClearable
                      />
                    </Col>
                  </Row>
                )}
                <Row>
                  <Col md="8" className="elem-field">
                    <Label className="text-sm-right">Склад транзитный</Label>
                    <Select
                      placeholder="Выберите склад"
                      className="react-select-container"
                      classNamePrefix="react-select"
                      options={warehouses.map((w) => ({ label: w.name, value: w.id }))}
                      value={(() => {
                        const id = values.defaultWarehouse;
                        if (!id) return null;
                        const w = warehouses.find(
                          (w) => String(w.id) === String(id)
                        );
                        if (w) return { label: w.name, value: w.id };
                        return {
                          label: String(id),
                          value: id,
                        };
                      })()}
                      onChange={(val) => setFieldValue("defaultWarehouse", val?.value ?? "")}
                      isClearable
                    />
                  </Col>
                </Row>
                <Row>
                  <Col md="8" className="elem-field">
                    <Label className="text-sm-right">Склад хранения</Label>
                    <Select
                      placeholder="Выберите склад"
                      className="react-select-container"
                      classNamePrefix="react-select"
                      options={warehouses.map((w) => ({ label: w.name, value: w.id }))}
                      value={(() => {
                        const w = warehouses.find((w) => w.id === values.storageWarehouse);
                        if (w) return { label: w.name, value: w.id };
                        if (values.storageWarehouse)
                          return {
                            label: values.storageWarehouse,
                            value: values.storageWarehouse,
                          };
                        return null;
                      })()}
                      onChange={(val) => setFieldValue("storageWarehouse", val?.value ?? "")}
                      isClearable
                    />
                  </Col>
                </Row>
                <Row>
                  <Col md="8" className="elem-field">
                    <Label className="text-sm-right">Телефон</Label>
                    <InputMask
                      mask="+7(999) 999-99-99"
                      value={values.phone}
                      onChange={(e) => setFieldValue("phone", e.target.value)}
                      className="form-control"
                    >
                      {(inputProps) => <Input {...inputProps} type="tel" placeholder="+7(999) 999-99-99" />}
                    </InputMask>
                  </Col>
                </Row>
                <Row>
                  <Col md="8" className="elem-field">
                    <Label className="text-sm-right">Пункт выдачи (адрес)</Label>
                    <Input
                      type="text"
                      placeholder="г. Москва, ..."
                      value={values.pickup}
                      onChange={(e) => setFieldValue("pickup", e.target.value)}
                    />
                  </Col>
                </Row>

                {arrayField(
                  "emailsForOrders",
                  "Email уведомления для новых заказов (дополнительно к ведущему менеджеру)",
                  "email@example.com",
                  values,
                  setFieldValue
                )}

                <Row>
                  <Col md="8" className="elem-field">
                    <Label className="text-sm-right">Наш пользователь</Label>
                    <Input
                      type="text"
                      placeholder="ourUser"
                      value={values.ourUser}
                      onChange={(e) => setFieldValue("ourUser", e.target.value)}
                    />
                  </Col>
                </Row>
                {branchIndex === 1 && (
                  <Row>
                    <Col md="8" className="elem-field">
                      <Label className="text-sm-right">Наш поставщик</Label>
                      <SelectProvider
                        provider={ourProviderOption}
                        setProvider={(val) => {
                          setFieldValue("ourProvider", val?.value ?? "");
                          setOurProviderOption(val ?? null);
                        }}
                      />
                    </Col>
                  </Row>
                )}
                <Row>
                  <Col md="8" className="elem-field">
                    <Label className="text-sm-right">Кладовщик по умолчанию</Label>
                    <Select
                      placeholder="Выберите кладовщика"
                      className="react-select-container"
                      classNamePrefix="react-select"
                      options={(admins ?? []).filter((a) => a.department === "warehouse")}
                      value={
                        (admins ?? []).find((a) => a.value === values.defaultStoreKeeper) ?? null
                      }
                      onChange={(val) => setFieldValue("defaultStoreKeeper", val?.value ?? "")}
                      isClearable
                    />
                  </Col>
                </Row>

                {arrayField(
                  "emailReportMinStock",
                  "Email отчёта по минимальному остатку",
                  "email@example.com",
                  values,
                  setFieldValue
                )}

                {branchIndex === 1 &&
                  arrayField(
                    "newRegistrationUser",
                    "Email при новой регистрации пользователя",
                    "email@example.com",
                    values,
                    setFieldValue
                  )}

                {warehouseArrayField(
                  "warehousesSearchAdmin",
                  "Склады поиска (админка)",
                  values,
                  setFieldValue
                )}

                {warehouseArrayField(
                  "warehousesSearchCustomer",
                  "Склады поиска (клиент)",
                  values,
                  setFieldValue
                )}

                <Row className="mt-3">
                  <Col md="12">
                    <h5 className="mb-2">Анализ цен наличия (market price)</h5>
                    <p className="text-muted small mb-2">
                      Склады и поставщики для проверки и обновления цен нашего наличия (команда app:marketPriceAnalysis).
                    </p>
                  </Col>
                </Row>
                {warehouseArrayField(
                  "marketPriceWarehouses",
                  "Склады для анализа цен",
                  values,
                  setFieldValue
                )}
                {providerArrayField(
                  "marketPriceProviders",
                  "Поставщики для выбора минимальной цены",
                  values,
                  setFieldValue
                )}
              </CardBody>
            </Card>

            <div className="settings__save">
              <Button type="submit" color="primary" className="button-wide">
                Сохранить
              </Button>
            </div>
          </form>
        )}
      </Formik>
    </Container>
  );
};
